// v3.15-1120 — Business Funding Operator Dashboard.
//
// • List of applications (search by app_id / business / contact / phone / email).
// • Status filter.
// • "+ New Application" modal — the Business Funding intake form swap from
//   the standard client schema. Used both as a standalone entry point AND
//   for any company whose industry_category === "business_funding"
//   (handled in the client-creation flow elsewhere — this page is the
//   master dashboard per spec Part 7).
// • Detail drawer — Application ID prominent at top, all 11 BF fields,
//   authorization status, workflow status dropdown, activity timeline.

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Loader2, Plus, Search, X, Building2, CheckCircle2, AlertCircle, Hash, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const STATUS_TONES = {
  'New':             'bg-slate-100 text-slate-700',
  'Submitted':       'bg-blue-100 text-blue-800',
  'Sent To Partner': 'bg-indigo-100 text-indigo-800',
  'In Review':       'bg-amber-100 text-amber-800',
  'Approved':        'bg-emerald-100 text-emerald-800',
  'Declined':        'bg-rose-100 text-rose-800',
  'Funded':          'bg-emerald-200 text-emerald-900 font-semibold',
  'Closed':          'bg-slate-200 text-slate-600',
};

const fmtMoney = (n) =>
  n == null || n === '' ? '—'
    : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

const StatusBadge = ({ value }) => (
  <span
    className={`inline-flex items-center text-[11px] font-medium px-2 py-1 rounded ${STATUS_TONES[value] || 'bg-slate-100 text-slate-700'}`}
    data-testid={`bf-status-badge-${value}`}
  >
    {value}
  </span>
);

// ─────────────────────────────────────────────────────────────────────
// New Application Modal
// ─────────────────────────────────────────────────────────────────────
const NewApplicationModal = ({ open, onClose, bfCompanies, presetCompanyId, onCreated }) => {
  const [form, setForm] = useState(() => ({
    application_id: '', business_name: '', contact_name: '',
    phone_number: '', email_address: '', business_state: '',
    business_industry: '', amount_requested: '', time_in_business: '',
    average_monthly_revenue: '', additional_comments: '',
    authorization_accepted: false, company_id: '', status: 'New',
  }));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Preset wins; else fall back to the first BF company (BTC after seed).
    setForm((f) => ({
      ...f,
      company_id: presetCompanyId || f.company_id || bfCompanies[0]?.id || '',
    }));
  }, [open, bfCompanies, presetCompanyId]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.application_id.trim()) {
      toast.error('Application ID is required (EQF confirmation number)');
      return;
    }
    if (!form.business_name.trim()) {
      toast.error('Business name is required');
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        amount_requested: form.amount_requested === '' ? null : parseFloat(form.amount_requested),
        average_monthly_revenue: form.average_monthly_revenue === '' ? null : parseFloat(form.average_monthly_revenue),
        company_id: form.company_id || null,
        company_name: bfCompanies.find((c) => c.id === form.company_id)?.name || null,
      };
      const { data } = await api.post('/business-funding/applications', body);
      toast.success('Application created');
      onCreated(data.item);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create application');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40"
      onClick={onClose}
      data-testid="bf-new-app-modal-backdrop"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-[820px] max-w-[95vw] max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="bf-new-app-modal"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div>
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-indigo-600" />
              New Business Funding Application
            </h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Business Funding schema — replaces standard client intake fields per spec Part 2.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="bf-new-app-close-btn">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto space-y-4">
          {/* Application ID — REQUIRED, prominent. Stays editable on
              CREATE only; the spec marks it immutable once stored. */}
          <div className="border-2 border-indigo-200 rounded p-3 bg-indigo-50/40">
            <Label className="text-[10px] uppercase tracking-wide text-indigo-700 font-semibold">
              Application ID / EQF Confirmation Number *
            </Label>
            <Input
              value={form.application_id}
              onChange={(e) => set('application_id', e.target.value)}
              placeholder="e.g. EQF-WOU0BDBG"
              className="mt-1 font-mono text-sm h-10"
              data-testid="bf-new-app-id"
              autoFocus
            />
            <p className="text-[10px] text-indigo-700 mt-1">
              Becomes immutable once saved. Use the exact value EQF provided.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Business Name *</Label>
              <Input value={form.business_name} onChange={(e) => set('business_name', e.target.value)} className="h-9 text-sm mt-1" data-testid="bf-new-app-business-name" />
            </div>
            <div>
              <Label className="text-xs">Contact Name</Label>
              <Input value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} className="h-9 text-sm mt-1" data-testid="bf-new-app-contact-name" />
            </div>
            <div>
              <Label className="text-xs">Phone Number</Label>
              <Input value={form.phone_number} onChange={(e) => set('phone_number', e.target.value)} className="h-9 text-sm mt-1" data-testid="bf-new-app-phone" />
            </div>
            <div>
              <Label className="text-xs">Email Address</Label>
              <Input type="email" value={form.email_address} onChange={(e) => set('email_address', e.target.value)} className="h-9 text-sm mt-1" data-testid="bf-new-app-email" />
            </div>
            <div>
              <Label className="text-xs">Business State</Label>
              <Input value={form.business_state} onChange={(e) => set('business_state', e.target.value)} placeholder="e.g. FL" className="h-9 text-sm mt-1" data-testid="bf-new-app-state" />
            </div>
            <div>
              <Label className="text-xs">Business Industry</Label>
              <Input value={form.business_industry} onChange={(e) => set('business_industry', e.target.value)} placeholder="e.g. Roofing" className="h-9 text-sm mt-1" data-testid="bf-new-app-business-industry" />
            </div>
            <div>
              <Label className="text-xs">Amount Requested</Label>
              <Input type="number" value={form.amount_requested} onChange={(e) => set('amount_requested', e.target.value)} placeholder="0" className="h-9 text-sm mt-1" data-testid="bf-new-app-amount" />
            </div>
            <div>
              <Label className="text-xs">Time In Business</Label>
              <Input value={form.time_in_business} onChange={(e) => set('time_in_business', e.target.value)} placeholder="e.g. 3 years" className="h-9 text-sm mt-1" data-testid="bf-new-app-tib" />
            </div>
            <div>
              <Label className="text-xs">Average Monthly Revenue</Label>
              <Input type="number" value={form.average_monthly_revenue} onChange={(e) => set('average_monthly_revenue', e.target.value)} placeholder="0" className="h-9 text-sm mt-1" data-testid="bf-new-app-amr" />
            </div>
            <div>
              <Label className="text-xs">Funding Partner</Label>
              <Select value={form.company_id} onValueChange={(v) => set('company_id', v)}>
                <SelectTrigger className="h-9 text-sm mt-1" data-testid="bf-new-app-company">
                  <SelectValue placeholder="Select partner..." />
                </SelectTrigger>
                <SelectContent>
                  {bfCompanies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs">Additional Comments</Label>
            <Textarea
              value={form.additional_comments}
              onChange={(e) => set('additional_comments', e.target.value)}
              rows={3}
              className="text-sm mt-1"
              data-testid="bf-new-app-comments"
            />
          </div>

          {/* Authorization checkbox — REQUIRED storage per spec Part 2 */}
          <label className="flex items-start gap-2 p-3 border rounded bg-slate-50 cursor-pointer" data-testid="bf-new-app-auth-label">
            <input
              type="checkbox"
              checked={form.authorization_accepted}
              onChange={(e) => set('authorization_accepted', e.target.checked)}
              className="mt-0.5"
              data-testid="bf-new-app-auth-checkbox"
            />
            <span className="text-xs text-slate-700">
              I authorize EasyQuickFund and its funding partners to contact me regarding available business financing options.
            </span>
          </label>
        </div>

        <div className="px-5 py-3 border-t bg-slate-50 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="bf-new-app-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="bf-new-app-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Application'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// v3.15-1120: BF detail row (label/value grid). Module-scope so React
// doesn't see a new component type on each render of ApplicationDetail.
const BfRow = ({ label, value, mono, testid }) => (
  <div className="grid grid-cols-3 gap-3 py-1.5 border-b last:border-0">
    <div className="text-[11px] uppercase tracking-wide text-slate-500 col-span-1">{label}</div>
    <div className={`text-sm text-slate-800 col-span-2 ${mono ? 'font-mono' : ''}`} data-testid={testid}>
      {value == null || value === '' ? <span className="text-slate-300">—</span> : value}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────
// Detail Drawer
// ─────────────────────────────────────────────────────────────────────
const ApplicationDetail = ({ open, app, statuses, onClose, onUpdated }) => {
  const [activities, setActivities] = useState([]);
  const [loadingActs, setLoadingActs] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  // v3.15-1182 — concierge assignment + note composer
  const [concierges, setConcierges] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    if (!open || !app?.id) return;
    let cancelled = false;
    setLoadingActs(true);
    api.get(`/business-funding/applications/${app.id}`)
      .then(({ data }) => { if (!cancelled) setActivities(data.activities || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingActs(false); });
    return () => { cancelled = true; };
  }, [open, app?.id]);

  // v3.15-1182 — load concierge candidates once per open
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.get('/business-funding/concierges')
      .then(({ data }) => { if (!cancelled) setConcierges(data.items || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open]);

  if (!open || !app) return null;

  const changeStatus = async (newStatus) => {
    if (newStatus === app.status) return;
    setStatusChanging(true);
    try {
      const { data } = await api.patch(`/business-funding/applications/${app.id}/status`, { status: newStatus });
      onUpdated(data.item);
      // Refresh activities so the new status_changed row appears.
      const { data: detail } = await api.get(`/business-funding/applications/${app.id}`);
      setActivities(detail.activities || []);
      toast.success(`Status → ${newStatus}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to change status');
    } finally {
      setStatusChanging(false);
    }
  };

  // v3.15-1182 — assign / unassign concierge
  const changeAssignee = async (newId) => {
    if ((app.assigned_to_id || '') === (newId || '')) return;
    setAssigning(true);
    try {
      const picked = concierges.find((c) => c.id === newId);
      const { data } = await api.patch(`/business-funding/applications/${app.id}/assign`, {
        assigned_to_id:   newId || null,
        assigned_to_name: picked?.full_name || null,
      });
      onUpdated(data.item);
      const { data: detail } = await api.get(`/business-funding/applications/${app.id}`);
      setActivities(detail.activities || []);
      toast.success(newId ? `Assigned to ${picked?.full_name || 'concierge'}` : 'Unassigned');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to assign concierge');
    } finally {
      setAssigning(false);
    }
  };

  // v3.15-1182 — add an operator note (stored as a timeline activity)
  const addNote = async () => {
    const body = noteDraft.trim();
    if (!body) return;
    setSavingNote(true);
    try {
      const { data } = await api.post(`/business-funding/applications/${app.id}/notes`, { body });
      setActivities(data.activities || []);
      setNoteDraft('');
      toast.success('Note added');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add note');
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40"
      onClick={onClose}
      data-testid="bf-detail-modal-backdrop"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-[860px] max-w-[95vw] max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="bf-detail-modal"
      >
        {/* Application ID — prominent header per spec Part 7 */}
        <div className="px-5 py-4 border-b bg-gradient-to-r from-indigo-50 to-white">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-indigo-600 font-semibold flex items-center gap-1.5">
                <Hash className="h-3 w-3" /> Application ID
              </div>
              <div className="font-mono text-2xl text-slate-900 mt-0.5 truncate" data-testid="bf-detail-application-id">
                {app.application_id}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {app.business_name} • {app.company_name}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* v3.15-1182 — Concierge assignment select */}
              <Select
                value={app.assigned_to_id || '__unassigned__'}
                onValueChange={(v) => changeAssignee(v === '__unassigned__' ? null : v)}
                disabled={assigning}
              >
                <SelectTrigger className="h-9 text-sm w-52" data-testid="bf-detail-concierge-select">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__unassigned__" data-testid="bf-concierge-option-unassigned">
                    Unassigned
                  </SelectItem>
                  {concierges.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`bf-concierge-option-${c.id}`}>
                      {c.full_name || c.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={app.status} onValueChange={changeStatus} disabled={statusChanging}>
                <SelectTrigger className="h-9 text-sm w-44" data-testid="bf-detail-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="bf-detail-close-btn">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 overflow-y-auto space-y-4">
          {/* All BF fields (spec Part 7). Standard client fields intentionally omitted. */}
          <div className="border rounded p-3" data-testid="bf-detail-fields">
            <BfRow label="Business Name"      value={app.business_name}     testid="bf-detail-business-name" />
            <BfRow label="Contact Name"       value={app.contact_name}       testid="bf-detail-contact-name" />
            <BfRow label="Phone Number"       value={app.phone_number}       testid="bf-detail-phone" />
            <BfRow label="Email Address"      value={app.email_address}      testid="bf-detail-email" />
            <BfRow label="Business State"     value={app.business_state}     testid="bf-detail-state" />
            <BfRow label="Business Industry"  value={app.business_industry}  testid="bf-detail-business-industry" />
            <BfRow label="Amount Requested"   value={fmtMoney(app.amount_requested)}        testid="bf-detail-amount" />
            <BfRow label="Time In Business"   value={app.time_in_business}   testid="bf-detail-tib" />
            <BfRow label="Avg Monthly Revenue" value={fmtMoney(app.average_monthly_revenue)} testid="bf-detail-amr" />
            <BfRow label="Additional Comments" value={app.additional_comments} testid="bf-detail-comments" />
            <BfRow
              label="Authorization"
              value={app.authorization_accepted
                ? (<span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Accepted</span>)
                : (<span className="inline-flex items-center gap-1 text-amber-700"><AlertCircle className="h-4 w-4" /> Not Accepted</span>)}
              testid="bf-detail-auth"
            />
            <BfRow label="Funding Partner"    value={app.company_name}       testid="bf-detail-company" />
            <BfRow
              label="Assigned Concierge"
              value={app.assigned_to_name || app.assigned_to || null}
              testid="bf-detail-assigned"
            />
            <BfRow label="Industry Type"      value={app.industry_type}      testid="bf-detail-industry-type" />
            <BfRow label="Source"             value={app.source === 'eqf_intake' ? 'EasyQuickFund' : (app.source || '—')} testid="bf-detail-source" />
            <BfRow label="Created"            value={fmtDate(app.created_at)} testid="bf-detail-created" />
            <BfRow label="Updated"            value={fmtDate(app.updated_at)} testid="bf-detail-updated" />
          </div>

          {/* Activity timeline — single source of truth per v3.15-1182 */}
          <div className="border rounded p-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-700">Activity</h3>
              <span className="text-[10px] text-slate-400" data-testid="bf-activity-count">
                {activities.length} {activities.length === 1 ? 'event' : 'events'}
              </span>
            </div>

            {/* Note composer — posts to /notes which writes a `note_added`
                activity. No separate notes store; the timeline is the
                single source of truth. */}
            <div className="mb-3 flex items-start gap-2" data-testid="bf-note-composer">
              <Textarea
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="Add a note to the timeline…"
                rows={2}
                className="text-sm flex-1"
                data-testid="bf-note-input"
              />
              <Button
                onClick={addNote}
                disabled={savingNote || !noteDraft.trim()}
                size="sm"
                data-testid="bf-note-submit"
              >
                {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Note'}
              </Button>
            </div>

            {loadingActs && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
            {!loadingActs && activities.length === 0 && (
              <p className="text-xs text-slate-400 italic">No activity recorded.</p>
            )}
            <ul className="space-y-2" data-testid="bf-detail-activities">
              {activities.map((a) => {
                const isNote = a.activity_type === 'note_added';
                const tone =
                  a.activity_type === 'status_changed'        ? 'border-amber-200 bg-amber-50' :
                  a.activity_type === 'concierge_assigned' ||
                  a.activity_type === 'concierge_reassigned' ||
                  a.activity_type === 'concierge_unassigned' ? 'border-indigo-200 bg-indigo-50' :
                  a.activity_type === 'application_received'  ? 'border-emerald-200 bg-emerald-50' :
                  isNote                                       ? 'border-slate-200 bg-slate-50' :
                                                                 'border-slate-100 bg-white';
                const actor = a.by_user_name || a.by_user_email || (a.by_source === 'system' ? 'System' : '—');
                return (
                  <li
                    key={a.id}
                    className={`text-xs border rounded px-3 py-2 ${tone}`}
                    data-testid={`bf-activity-${a.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold"
                        data-testid={`bf-activity-type-${a.id}`}
                      >
                        {a.activity_type?.replace(/_/g, ' ')}
                      </span>
                      <span className="text-[10px] text-slate-400 font-mono">{fmtDate(a.created_at)}</span>
                    </div>
                    <div className={`mt-1 text-slate-800 ${isNote ? 'whitespace-pre-wrap' : ''}`}>
                      {a.description}
                    </div>
                    <div className="mt-1 flex items-center justify-between text-[10px] text-slate-500">
                      <span data-testid={`bf-activity-actor-${a.id}`}>by {actor}</span>
                      {a.metadata?.source && (
                        <span className="text-slate-400">via {a.metadata.source}</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────
export default function BusinessFundingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [statuses, setStatuses] = useState([
    'New', 'Submitted', 'Sent To Partner', 'In Review',
    'Approved', 'Declined', 'Funded', 'Closed',
  ]);
  const [bfCompanies, setBfCompanies] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [presetCompanyId, setPresetCompanyId] = useState(null);
  const [detailApp, setDetailApp] = useState(null);

  // v3.15-1120: If routed here from the standard client-create flow with
  // `?new=1&company_id=…`, auto-open the New Application modal with that
  // partner pre-selected. Strip the query so refresh doesn't re-trigger.
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      const cid = searchParams.get('company_id') || '';
      setPresetCompanyId(cid || null);
      setShowNew(true);
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      next.delete('company_id');
      setSearchParams(next, { replace: true });
    }

  }, []);

  // Load BF companies — anything with industry_category=business_funding.
  useEffect(() => {
    let cancelled = false;
    api.get('/companies', { params: { industry_category: 'business_funding' } })
      .then(({ data }) => {
        if (cancelled) return;
        // Backend may return an array directly OR a paginated shape.
        const items = Array.isArray(data) ? data : (data.items || data.companies || []);
        const filtered = items.filter((c) => (c.industry_category || c.company_type) === 'business_funding');
        setBfCompanies(filtered);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Load BF statuses from backend (single source of truth).
  useEffect(() => {
    let cancelled = false;
    api.get('/business-funding/statuses')
      .then(({ data }) => { if (!cancelled && Array.isArray(data?.items)) setStatuses(data.items); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const loadApps = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data } = await api.get('/business-funding/applications', { params });
      setApps(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => { loadApps(); }, [statusFilter]);

  // Status counters for the header.
  const counts = useMemo(() => {
    const c = { total: apps.length };
    for (const s of statuses) c[s] = 0;
    for (const a of apps) c[a.status] = (c[a.status] || 0) + 1;
    return c;
  }, [apps, statuses]);

  const onSearchKey = (e) => {
    if (e.key === 'Enter') loadApps();
  };

  return (
    <div className="max-w-[1600px] mx-auto space-y-4" data-testid="business-funding-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800 flex items-center gap-2">
            <Building2 className="h-6 w-6 text-indigo-600" />
            Business Funding Applications
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            EQF → Hub → Funding Partner. Application ID is the primary cross-reference.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadApps} disabled={loading} data-testid="bf-refresh-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Refresh
          </Button>
          <Button onClick={() => setShowNew(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="bf-new-app-btn">
            <Plus className="h-4 w-4 mr-1" /> New Application
          </Button>
        </div>
      </div>

      {/* Counters strip */}
      <div className="grid grid-cols-2 md:grid-cols-9 gap-2 mb-4" data-testid="bf-counters">
        <div className="border rounded px-3 py-2 bg-slate-50">
          <div className="text-[10px] uppercase text-slate-500">Total</div>
          <div className="text-lg font-semibold text-slate-800" data-testid="bf-count-total">{counts.total}</div>
        </div>
        {statuses.map((s) => (
          <div key={s} className="border rounded px-3 py-2">
            <div className="text-[10px] uppercase text-slate-500 truncate">{s}</div>
            <div className="text-lg font-semibold text-slate-800" data-testid={`bf-count-${s}`}>{counts[s] || 0}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={onSearchKey}
            placeholder="Search Application ID, business, contact, phone, email…"
            className="h-9 text-sm pl-8"
            data-testid="bf-search-input"
          />
        </div>
        <Button variant="outline" onClick={loadApps} size="sm" className="h-9" data-testid="bf-search-btn">Search</Button>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 text-sm w-44" data-testid="bf-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {statuses.map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="border rounded overflow-hidden">
        <table className="w-full text-sm" data-testid="bf-applications-table">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-slate-600">Application ID</th>
              <th className="text-left px-3 py-2 font-medium text-slate-600">Business</th>
              <th className="text-left px-3 py-2 font-medium text-slate-600">Contact</th>
              <th className="text-left px-3 py-2 font-medium text-slate-600">Phone</th>
              <th className="text-left px-3 py-2 font-medium text-slate-600">Amount</th>
              <th className="text-left px-3 py-2 font-medium text-slate-600">Partner</th>
              <th className="text-left px-3 py-2 font-medium text-slate-600">Assigned</th>
              <th className="text-left px-3 py-2 font-medium text-slate-600">Status</th>
              <th className="text-left px-3 py-2 font-medium text-slate-600">Received</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</td></tr>
            )}
            {!loading && apps.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-400 italic">No applications match your filters.</td></tr>
            )}
            {!loading && apps.map((a) => (
              <tr
                key={a.id}
                className="border-b last:border-0 hover:bg-indigo-50/30 cursor-pointer"
                onClick={() => setDetailApp(a)}
                data-testid={`bf-row-${a.application_id}`}
              >
                <td className="px-3 py-2 font-mono text-xs text-indigo-700">{a.application_id}</td>
                <td className="px-3 py-2 text-slate-800 font-medium">{a.business_name || <span className="text-slate-300">—</span>}</td>
                <td className="px-3 py-2 text-slate-700">{a.contact_name || <span className="text-slate-300">—</span>}</td>
                <td className="px-3 py-2 text-slate-700">{a.phone_number || <span className="text-slate-300">—</span>}</td>
                <td className="px-3 py-2 text-slate-700">{fmtMoney(a.amount_requested)}</td>
                <td className="px-3 py-2 text-slate-600 text-xs">{a.company_name || <span className="text-slate-300">—</span>}</td>
                <td
                  className="px-3 py-2 text-slate-600 text-xs"
                  data-testid={`bf-row-assigned-${a.application_id}`}
                >
                  {a.assigned_to_name || a.assigned_to || <span className="text-slate-300">—</span>}
                </td>
                <td className="px-3 py-2"><StatusBadge value={a.status} /></td>
                <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(a.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <NewApplicationModal
        open={showNew}
        onClose={() => setShowNew(false)}
        bfCompanies={bfCompanies}
        presetCompanyId={presetCompanyId}
        onCreated={(item) => { setShowNew(false); setPresetCompanyId(null); setApps((prev) => [item, ...prev]); setDetailApp(item); }}
      />

      <ApplicationDetail
        open={!!detailApp}
        app={detailApp}
        statuses={statuses}
        onClose={() => setDetailApp(null)}
        onUpdated={(updated) => {
          setApps((prev) => prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)));
          setDetailApp(updated);
        }}
      />
    </div>
  );
}
